import { lazy, useEffect, useState } from "react"
import { useAtom } from "jotai"
import { useLocation } from "react-router-dom"
import FranchiseePendingTopayRptTableNew from "../content/AcountingReport/FranchiseePendingTopayReport/FranchiseePendingTopayRptTableNew"
import BranchWiseBalanceTable from "../content/AcountingReport/BankingReport/BranchWiseBalanceTable"
import UserWiseBalanceTable from "../content/AcountingReport/BankingReport/UserWiseBalanceTable"
import BusMemo from "../content/AcountingReport/BusAndCargoMemo/BusMemo"
import CargoMemo from "../content/AcountingReport/BusAndCargoMemo/CargoMemo"
import BusAndCargoMemoTable from "../content/AcountingReport/BusAndCargoMemo/BusAndCargoMemoTable"
import { getBranchWiseBalanceReportData, getUserWiseBalanceReportData } from "../content/AcountingReport/BankingReport/constants"
import { getBranchCollectionData } from "../content/AcountingReport/BranchwiseCollectionAndDispatchMemoCollection/constants"
import {getFranchiseePendingTopayData } from "../content/AcountingReport/FranchiseePendingTopayReport/constants"
import { alertAtom } from "./jotaiState/globalStateAtoms"
import { urls } from "./constants"
import { GetRequest, PostRequest } from "./api"
const BranchwiseCollectionAndDispatchMemoCollectionTable = lazy(() =>
    import('../content/AcountingReport/BranchwiseCollectionAndDispatchMemoCollection/BranchwiseCollectionAndDispatchMemoCollectionTable')
)
const FranchiseeSettlementViewTables = lazy(() =>
    import('../content/AcountingReport/FranchiseeSettlementReport/FranchiseeSettlementViewTables')
)
const REPORT_DB_NAME = "cargo-report-cache"
const REPORT_DB_STORE = "reports"
const MAX_INDEXED_DB_REPORTS = 10

const openReportDb = () => {
    return new Promise((resolve) => {
        if (typeof window === "undefined" || !window.indexedDB) {
            resolve(null)
            return
        }

        const request = window.indexedDB.open(REPORT_DB_NAME, 1)

        request.onupgradeneeded = () => {
            const database = request.result
            if (!database.objectStoreNames.contains(REPORT_DB_STORE)) {
                database.createObjectStore(REPORT_DB_STORE, { keyPath: "id" })
            }
        }

        request.onsuccess = () => resolve(request.result)
        request.onerror = () => {
            console.error("Failed to open report cache database", request.error)
            resolve(null)
        }
    })
}

const getIndexedDbReport = async (rid) => {
    const database = await openReportDb()
    if (!database) return null

    return new Promise((resolve) => {
        const transaction = database.transaction(REPORT_DB_STORE, "readonly")
        const request = transaction.objectStore(REPORT_DB_STORE).get(rid)

        request.onsuccess = () => {
            resolve(request.result?.payload || null)
        }

        request.onerror = () => {
            console.error("Failed to read cached report from IndexedDB", request.error)
            resolve(null)
        }

        transaction.oncomplete = () => {
            database.close()
        }

        transaction.onabort = transaction.onerror = () => {
            database.close()
        }
    })
}

const pruneIndexedDbReports = async (activeRid) => {
    const database = await openReportDb()
    if (!database) return

    await new Promise((resolve) => {
        const transaction = database.transaction(REPORT_DB_STORE, "readwrite")
        const reportStore = transaction.objectStore(REPORT_DB_STORE)

        if (typeof reportStore.getAllKeys !== "function") {
            resolve()
            return
        }

        const request = reportStore.getAllKeys()

        request.onsuccess = () => {
            const reportKeys = (request.result || [])
                .map((key) => String(key))
                .sort((left, right) => Number(left) - Number(right))
            const removableKeys = reportKeys.filter((key) => key !== activeRid)
            const excessCount = Math.max(0, reportKeys.length - MAX_INDEXED_DB_REPORTS)

            removableKeys.slice(0, excessCount).forEach((key) => {
                reportStore.delete(key)
            })
        }

        request.onerror = () => {
            console.error("Failed to prune IndexedDB report cache", request.error)
        }

        transaction.oncomplete = () => {
            database.close()
            resolve()
        }

        transaction.onabort = transaction.onerror = () => {
            database.close()
            resolve()
        }
    })
}

const cacheReportInIndexedDb = async (rid, reportData) => {
    const database = await openReportDb()
    if (!database) return false

    const didPersist = await new Promise((resolve) => {
        const transaction = database.transaction(REPORT_DB_STORE, "readwrite")
        const reportStore = transaction.objectStore(REPORT_DB_STORE)

        reportStore.put({
            id: rid,
            createdAt: Date.now(),
            payload: reportData,
        })

        transaction.oncomplete = () => {
            database.close()
            resolve(true)
        }

        transaction.onabort = transaction.onerror = () => {
            console.error("Failed to cache report in IndexedDB", transaction.error)
            database.close()
            resolve(false)
        }
    })

    if (didPersist) {
        await pruneIndexedDbReports(rid)
    }

    return didPersist
}

const persistReport = async (rid, reportData) => {
    const indexedDbStored = await cacheReportInIndexedDb(rid, reportData)
    return indexedDbStored
}

const ViewReportV2 = () => {
    const location = useLocation()
    const rid = new URLSearchParams(location.search).get("rid")
    const [report, setReport] = useState(null)
    const [, setAlert] = useAtom(alertAtom)
    const [isPdfLoading, setIsPdfLoading] = useState(false)


    useEffect(() => {
        let isMounted = true

        const loadReport = async () => {
            const rid = new URLSearchParams(location.search).get("rid")

            if (!rid) {
                if (isMounted) {
                    setReport(null)
                }
                return
            }

            let reportData = await getIndexedDbReport(rid)

            if (!reportData && window.opener?.generatedReports?.[rid]) {
                reportData = JSON.parse(JSON.stringify(window.opener.generatedReports[rid]))
            }

            if (isMounted) {
                setReport(reportData)
            }

            if (reportData && window.opener?.generatedReports?.[rid]) {
                const didPersist = await persistReport(rid, reportData)
                if (didPersist && window.opener?.generatedReports?.[rid]) {
                    delete window.opener.generatedReports[rid]
                }
            }
        }

        loadReport()

        return () => {
            isMounted = false
        }
    }, [location.search])

    const handleReportDataChange = async (nextReportData) => {
        if (!rid || !report) return

        const updatedReport = {
            ...report,
            reportData: nextReportData,
        }

        setReport(updatedReport)

        if (window.opener?.generatedReports?.[rid]) {
            window.opener.generatedReports[rid] = JSON.parse(JSON.stringify(updatedReport))
        }

        await persistReport(rid, updatedReport)
    }

    const refreshBranchWiseBalanceReport = async () => {
        if (!report?.saveMeta) return null

        const nextReportData = await getBranchWiseBalanceReportData(report.saveMeta, setAlert)
        if (!nextReportData) return null

        await handleReportDataChange(nextReportData)
        return nextReportData
    }

    const refreshUserWiseBalanceReport = async () => {
        if (!report?.saveMeta) return null

        const nextReportData = await getUserWiseBalanceReportData(report.saveMeta, setAlert)
        if (!nextReportData) return null

        await handleReportDataChange(nextReportData)
        return nextReportData
    }

    const refreshBranchWiseCollectionReport = async () => {
        if (!report?.saveMeta) return null

        const nextReportData = await getBranchCollectionData(report.saveMeta, setAlert)
        if (!nextReportData) return null

        await handleReportDataChange(nextReportData)
        return nextReportData
    }
   
    const refreshFranchiseePendingTopayReport = async () => {        
        if (!report?.saveMeta) return null

        const groupedData = await getFranchiseePendingTopayData(report.saveMeta, setAlert)
        if (!groupedData) return null

        await handleReportDataChange(groupedData)
        return groupedData
    }

    const refreshCargoMemoReport = async () => {
        if (!report?.saveMeta) return null

        const res =  await GetRequest(urls.getCargoMemoData, report.saveMeta)
        if (res.status === 'Success') {
            const rows = res?.Data?.Table1 || []
            await handleReportDataChange(rows)
            if (rows.length === 0) {
                setAlert({ show: true, msg: 'No Data Found', nextId: 'loadbtn' })
            }
            return rows
        }

        setAlert({ show: true, msg: res?.ErrMessage, nextId: 'loadbtn' })
        return null
    }

    if (!report) return null

    if (report.id === 'bus_memo_report') {
        return (
            <BusMemo
                fromDate={report.fromDate}
                selVehicle={report.selVehicle}
                reportData={report.reportData}
                updateAlertError={setAlert}
                loginData={report.loginData}
                isPdfLoading={isPdfLoading}
                setIsPdfLoading={setIsPdfLoading}
            />
        )
    }

    if (report.id === 'cargo_memo_report') {
        return (
            <CargoMemo
                fromDate={report.fromDate}
                selVehicle={report.selVehicle}
                selInclGSTComm={report.selInclGSTComm}
                selOfflineBooking={report.selOfflineBooking}
                selToPayHamali={report.selToPayHamali}
                updateAlertError={setAlert}
                getCargoMemoReportData={refreshCargoMemoReport}
                reportData={report.reportData}
                loginData={report.loginData}
                isPdfLoading={isPdfLoading}
                setIsPdfLoading={setIsPdfLoading}
                
            />
        )
    }

    if (report.id === 'bus_and_cargo_memo_report') {
        return (
            <BusAndCargoMemoTable
                fromDate={report.fromDate}
                selVehicle={report.selVehicle}
                selInclGSTComm={report.selInclGSTComm}
                selOfflineBooking={report.selOfflineBooking}
                selToPayHamali={report.selToPayHamali}
                updateAlertError={setAlert}
                getCargoMemoReportData={refreshCargoMemoReport}
                reportData={report.reportData}
                loginData={report.loginData}
                isPdfLoading={isPdfLoading}
                setIsPdfLoading={setIsPdfLoading}
               
            />
        )
    }

    if (report.id === 'branch_wise_and_dispatch_memo') {
        return (
            <BranchwiseCollectionAndDispatchMemoCollectionTable
                sourceCity={report.filterData.sourceCity}
                sourceBranch={report.filterData.sourceBranch}
                headerData={report.headerData}
                tableData={report.tableData}
                reportName={report.reportName}
                roleName={report.roleName}
                saveMeta={report.saveMeta}
                onRefreshReport={refreshBranchWiseCollectionReport}
            />
        )
    }

     if (report.id === 'franchisee_pending_to_pay_report') {
        return (
            <FranchiseePendingTopayRptTableNew
             reportName={report.reportName}
             reportData={report.reportData}
             filters={report.filters}
             loginData={report.loginData}
             columns={report.columns}
             excelColumns={report.excelColumns}
             allownFormatLR={report.allownFormatLR}
             saveMeta={report.saveMeta}
             onRefreshReport={refreshFranchiseePendingTopayReport}
            />
        )
    }
   
    if (report.id === 'franchisee_settlement_report') {
        return (
            <FranchiseeSettlementViewTables
                filters={report.filters}
                loginData={report.loginData}
                Table1={report.Table1}
                reportData={report.reportData}
                originalData={report.originalData}
                summaryReportData={report.summaryReportData}
                allownFormatLR={report.allownFormatLR}
                columns={report.columns}
                excelColumns={report.excelColumns}
            />
        )
    } 
     if (report.id === 'branch_wise_balance_report') {
        return (
            <BranchWiseBalanceTable
            reportName = {report.reportName}
            reportData={report.reportData}
            criteriaHeaders ={report.criteriaHeaders}
            criteriaData = {report.criteriaData}
            saveMeta={report.saveMeta}
            loginData={report.loginData}
            onRefreshReport={refreshBranchWiseBalanceReport}
            />
        )
    } 
    
    if (report.id === 'user_wise_balance_report') {
        return (
            <UserWiseBalanceTable
            reportName = {report.reportName}
            reportData={report.reportData}
            criteriaHeaders ={report.criteriaHeaders}
            criteriaData = {report.criteriaData}
            saveMeta={report.saveMeta}
            loginData={report.loginData}
            onRefreshReport={refreshUserWiseBalanceReport}
            />
        )
    }
    return null
}

export default ViewReportV2
